import React, { useContext, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { AppContext } from '@state/context'
import { closePanel } from '@state/actions/panelsActions'
import Button from '@components/Button'
import ButtonWithLoader from '@components/ButtonWithLoader'
import Heading from '@components/Heading'
import Divider from '@components/Divider'
import Form from '@components/Form'
import {
  PANELS,
  DIVIDER_SIZE,
  BUTTON_SIZE,
  HEADING_TYPE,
  BUTTON_TYPE,
  TEXT_SIZES,
} from '@helpers/constants'
import Text from '@components/Text'
import { useCurrentUser } from '@hooks/useCurrentUser'
import { useSchool } from '@hooks/useSchool'
import { useSortAndLabelStudentList } from '@hooks/useSortAndLabelStudentList'
import { useUpsertSchoolGroup } from '@hooks/mutations/useUpsertSchoolGroup'

const CreateSchoolGroupPanel = () => {
  const [, dispatch] = useContext(AppContext)
  const { currentUser } = useCurrentUser()
  const { school } = useSchool(currentUser?.schools[0].id)
  const [students, setStudents] = useState(undefined)
  const { studentOptions } = useSortAndLabelStudentList(students)
  const { post, isPosting } = useUpsertSchoolGroup()
  const { handleSubmit, errors, control, register, watch } = useForm()
  const watchName = watch('name')

  useEffect(() => {
    if (school) {
      let filteredStudents = school.students

      if (!currentUser?.hasPaidLicense) {
        const myClass = currentUser.schoolClasses.find((sc) => sc.isPrivate)
        filteredStudents = filteredStudents.filter(
          (student) =>
            Array.isArray(student.schoolClasses) &&
            student.schoolClasses.some((sc) => sc?.id === myClass.id) &&
            student.email === null &&
            student.username
        )
      } else {
        const currentUserClassIds = currentUser.schoolClasses
          .filter((sc) => !sc.isPrivate)
          .map((sc) => sc.id)

        filteredStudents = filteredStudents.filter(
          (student) =>
            student.email !== null &&
            !student.username && // student is not anonymous
            Array.isArray(student.schoolClasses) &&
            student.schoolClasses.some(
              (sc) => sc?.id && currentUserClassIds.includes(sc.id) // is in users classes
            )
        )
      }

      setStudents(filteredStudents)
    }
  }, [school])

  const onSubmit = async (data) => {
    const res = await post({
      name: data.name,
      students: { connect: data.students ?? [] },
      creator: { connect: currentUser.id },
      school: { connect: school.id },
    })

    if (res.success) dispatch(closePanel(PANELS.CREATE_SCHOOL_GROUP))
  }

  return (
    <>
      <div className='flex justify-between'>
        <div className={'flex flex-col'}>
          <Text>Mitt lärarrum - {school?.name ?? ''}</Text>
          <Heading headingLevel={HEADING_TYPE.H2} size={TEXT_SIZES.XLARGE}>
            Skapa grupp
          </Heading>
        </div>
        <div className={'self-start'}>
          <Button
            handleClick={() => {
              dispatch(closePanel(PANELS.CREATE_SCHOOL_GROUP))
            }}
            size={BUTTON_SIZE.NO_PADDING}
            icon={'Close'}
          />
        </div>
      </div>

      <Divider size={DIVIDER_SIZE.BASE} />

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Form.Label htmlFor='name'>Namnge din grupp</Form.Label>
        <Form.Input
          type={'text'}
          name='name'
          id='name'
          ref={register({ required: true })}
          placeholder=''
        />
        {errors.name && <Form.ErrorMessage>Namn behövs</Form.ErrorMessage>}

        <Divider size={DIVIDER_SIZE.SMALL} />

        <Form.Label htmlFor='classes'>Lägg till elever</Form.Label>
        <Controller
          control={control}
          defaultValue={null}
          name={'students'}
          render={({ onChange, name }) => (
            <Form.MultiSelect
              placeholder={'Sök efter elev'}
              name={name}
              onChange={(selectedOptions) => {
                onChange(
                  selectedOptions.map((option) => parseInt(option.value))
                )
              }}
              options={studentOptions}
              isDisabled={!studentOptions}
              maxWidth={'500px'}
              hideSelectedOptions={true}
              isMulti
              isSearchable
            />
          )}
        />
        {errors.classes && <Form.ErrorMessage />}

        <Divider size={DIVIDER_SIZE.BASE} />

        <ButtonWithLoader
          type={BUTTON_TYPE.PRIMARY}
          isLoading={isPosting}
          enabledText={'Skapa grupp'}
          disabled={isPosting || watchName === ''}
        />
      </Form>

      <Divider size={DIVIDER_SIZE.SMALL} />

      <Text bold>Bra att veta</Text>
      <Text>
        &bull; En grupp kan bestå av många, några eller en elev.
        <br />
        &bull; En elev kan inte se vilken grupp hen tillhör.
        <br />
        &bull; En elev kan inte se vilka andra elever som tillhör en viss grupp.
      </Text>
    </>
  )
}

export default CreateSchoolGroupPanel
